import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, UserCircle2, Wrench, Coins, ArrowRight, Info, ChevronDown, Bookmark, LogOut, AtSign, Image as ImageIcon, Sparkles, Send, Shield } from "lucide-react";
import { isAdmin, clearAdmin } from "../lib/admin";
import { fetchCoinBalance } from "../lib/coins";
import { useAuth } from "../lib/auth";
import AuthModal from "./AuthModal";
import NotificationsBell from "./NotificationsBell";
import AvatarDisplay, { AVATAR_ICONS } from "./profile/AvatarDisplay";
import { toast } from "sonner";

/** Coin pill in the navbar. Clicking opens an "Earning Key" popover showing
 *  every way to earn coins, the per-event payout, and today's cap progress.
 *  A "Spend on cards" CTA at the bottom routes to /vault-cards.
 *  Auto-refreshes every 25s and on the "coins-changed" window event. */
function CoinBalancePill() {
  const [data, setData] = useState(null);   // { balance, rules, recent }
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancel = false;
    const load = () => fetchCoinBalance().then((d) => { if (!cancel) setData(d); }).catch(() => {});
    load();
    const i = setInterval(load, 25000);
    const onChange = () => load();
    window.addEventListener("coins-changed", onChange);
    return () => { cancel = true; clearInterval(i); window.removeEventListener("coins-changed", onChange); };
  }, []);

  // Re-fetch fresh data each time the popover opens so today's caps are current.
  useEffect(() => {
    if (open) fetchCoinBalance().then(setData).catch(() => {});
  }, [open]);

  // Close on Escape + click outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, [open]);

  if (data === null) return null;
  const balance = data.balance ?? 0;
  const rules = data.rules || {};

  // Stable order — biggest payouts at top so the user sees the high-value
  // actions first. Tiebreak by label for determinism.
  const orderedKeys = Object.keys(rules).sort((a, b) => {
    const ca = rules[a]?.coins ?? 0;
    const cb = rules[b]?.coins ?? 0;
    if (cb !== ca) return cb - ca;
    return (rules[a]?.label || "").localeCompare(rules[b]?.label || "");
  });

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-coin-pill"
        title={`${balance} coins · click for earning key`}
        className="inline-flex items-center gap-1.5 px-2.5 py-2 border border-[#ffd60a]/40 text-[#ffd60a] hover:bg-[#ffd60a]/10 font-mono text-[10px] tracking-[0.15em] xl:tracking-[0.2em] uppercase transition-colors"
      >
        <Coins size={11} />
        <span data-testid="coin-balance-value">{balance.toLocaleString()}</span>
      </button>

      {open && (
        <div
          data-testid="nav-coin-popover"
          className="absolute right-0 mt-2 w-[340px] max-w-[90vw] z-[120] bg-[#050505] border border-[#ffd60a]/40 shadow-[0_0_40px_-10px_rgba(255,214,10,0.5)]"
        >
          <div className="h-[2px] w-full bg-[#ffd60a]" />
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-mono text-[9px] tracking-[0.25em] text-[#ffd60a] mb-1 flex items-center gap-1.5">
                  <Info size={10} /> EARNING KEY
                </div>
                <div className="font-heading text-2xl text-white leading-none">
                  {balance.toLocaleString()}
                  <span className="text-zinc-600 font-mono text-[10px] tracking-[0.2em] ml-2">COINS</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="nav-coin-popover-close"
                className="text-zinc-500 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>

            <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-2">
              // WAYS TO EARN · TODAY'S PROGRESS
            </div>

            <ul className="max-h-[360px] overflow-y-auto divide-y divide-white/[0.06] -mx-1">
              {orderedKeys.map((k) => {
                const r = rules[k] || {};
                const earned = r.today_earned || 0;
                const cap = r.daily_cap;
                const capped = cap != null && earned >= cap;
                return (
                  <li key={k} className="flex items-center gap-3 px-1 py-2">
                    <div className="font-mono text-[11px] text-[#ffd60a] w-10 shrink-0 tabular-nums">
                      +{r.coins}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-zinc-200 truncate">{r.label || k}</div>
                      <div className="font-mono text-[9px] tracking-[0.15em] text-zinc-500 mt-0.5">
                        {cap == null
                          ? "NO DAILY CAP"
                          : `TODAY ${earned}/${cap}${capped ? " · MAXED" : ""}`}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/vault-cards"); }}
                data-testid="nav-coin-spend-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-[#ffd60a] text-[#050505] hover:bg-white px-3 py-2 transition-colors"
              >
                Spend on Card Rolls <ArrowRight size={11} />
              </button>
            </div>
            <div className="mt-2 font-mono text-[8px] tracking-[0.2em] text-zinc-600 leading-relaxed">
              // CAPS RESET DAILY AT 00:00 UTC. SOME ACTIONS ARE SERVER-VALIDATED.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const NAV = [
  { to: "/archive", label: "Archive" },
  { to: "/deep-dive", label: "Deep Dive" },
  { to: "/connect-dots", label: "Connect Dots" },
  { to: "/equation", label: "Equation" },
  { to: "/threads", label: "Threads" },
  { to: "/warroom", label: "The Ties" },
  { to: "/vault-cards", label: "Cards" },
  { to: "/awakening", label: "Awakening" },
  { to: "/bookmarks", label: "Saved" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [adminCode, setAdminCodePresent] = useState(isAdmin());
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signup");

  // Admin UI unlocks if EITHER we have an admin code in localStorage (manual
  // admin code entry) OR the backend has told us this account is the platform
  // owner via /auth/me. The is_owner fallback is critical for cases where
  // /admin/whoami hasn't responded yet, or fails transiently — the owner
  // should never see a menu without an Admin link.
  const admin = adminCode || user?.is_owner === true;

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  useEffect(() => {
    const h = () => setAdminCodePresent(isAdmin());
    window.addEventListener("admin-changed", h);
    return () => window.removeEventListener("admin-changed", h);
  }, []);

  // Allow other pages (e.g., HomePage signup CTA) to open the auth modal.
  useEffect(() => {
    const openHandler = (e) => {
      const mode = e?.detail?.mode || "signup";
      setAuthMode(mode);
      setAuthOpen(true);
    };
    window.addEventListener("open-auth", openHandler);
    return () => window.removeEventListener("open-auth", openHandler);
  }, []);

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 lg:flex-initial">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2 sm:gap-3 group shrink-0">
            <div className="w-8 h-8 border border-[#ff3b30] flex items-center justify-center shrink-0">
              <span className="font-mono text-[#ff3b30] text-xs font-bold">VJ</span>
            </div>
            <div className="leading-none min-w-0 hidden 2xl:block">
              <div className="font-heading font-black uppercase tracking-tight text-white text-sm sm:text-base whitespace-nowrap">
                V. Jones Vault
              </div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 mt-0.5 whitespace-nowrap">
                DEEP / ARCHIVE / 2026
              </div>
            </div>
            {/* At the cramped xl breakpoint (1280–1535) we now show ONLY the
                VJ logo box. The wordmark + tagline only appear at 2xl+ where
                there's actually room without colliding with the nav tabs. */}
            {/* Below xl the nav menu is hidden behind the hamburger, so it's safe
                to show the full wordmark again. */}
            <div className="leading-none min-w-0 xl:hidden">
              <div className="font-heading font-black uppercase tracking-tight text-white text-sm sm:text-base whitespace-nowrap">
                V. Jones Vault
              </div>
            </div>
          </Link>
        </div>

        <nav className="hidden xl:flex items-center gap-0.5 xl:gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-2 xl:px-2.5 py-2 font-mono text-[11px] xl:text-xs uppercase tracking-[0.12em] xl:tracking-[0.15em] transition-colors ${
                  isActive
                    ? "text-[#ff3b30]"
                    : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          {/* Admin icons removed from the nav — they now live inside the
              profile dropdown so the header doesn't get squeezed when the
              user balance or notifications pip pushes everything together.
              The "Random file" shuffle button also lives on the homepage
              now, so it was removed here to free up nav space. */}
          {user ? (
            <>
            <CoinBalancePill />
            <NotificationsBell />
            <ProfileMenu user={user} admin={admin} />
            </>
          ) : (
            <button
              type="button"
              onClick={() => openAuth("signup")}
              data-testid="nav-signup-btn"
              title="Optional account — sync your saves"
              className="ml-1.5 inline-flex items-center gap-1.5 px-2.5 xl:px-3 py-2 whitespace-nowrap text-purple-300 hover:bg-purple-500/10 underline underline-offset-4 decoration-purple-500/60 font-mono text-[10px] tracking-[0.15em] xl:tracking-[0.2em] uppercase transition-colors shrink-0"
            >
              <UserCircle2 size={11} />
              <span className="hidden xl:inline">Sign up / In</span>
              <span className="xl:hidden">Sign in</span>
            </button>
          )}
        </nav>

        <div className="xl:hidden flex items-center gap-1 shrink-0">
          {user && <CoinBalancePill />}
          {user && <NotificationsBell />}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="text-zinc-300 p-1"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 bg-[#050505]">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] border-b border-white/5 ${
                  isActive ? "text-[#ff3b30]" : "text-zinc-400"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          {/* Mobile admin row */}
          {admin && (
            <>
              <Link
                to="/admin/manage"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-manage"
                className="block px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-[#a78bfa] border-b border-white/5"
              >
                <Wrench size={12} className="inline mr-2" /> Manage
              </Link>
              <button
                type="button"
                onClick={() => { clearAdmin(); setOpen(false); }}
                data-testid="mobile-admin-exit"
                className="block w-full text-left px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-[#ff3b30] border-b border-white/5"
              >
                <ShieldCheck size={12} className="inline mr-2" /> Admin — Exit
              </button>
            </>
          )}
          {/* Mobile auth — signed-in users see a profile link instead of a
              sign-out button. Sign-out was being mis-tapped (especially next
              to the avatar/profile button) and there's no real reason to
              expose it — sessions are anonymous-friendly anyway. */}
          {user ? (
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              data-testid="mobile-profile-link"
              className="block w-full text-left px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-purple-300 border-b border-white/5"
            >
              <UserCircle2 size={12} className="inline mr-2" />
              {user.username} — My Profile
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => { openAuth("signup"); setOpen(false); }}
              data-testid="mobile-signup-btn"
              className="block w-full text-left px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-purple-300 underline underline-offset-4 decoration-purple-500/60 border-b border-white/5"
            >
              <UserCircle2 size={12} className="inline mr-2" />
              Sign up / Sign in
            </button>
          )}
        </div>
      )}
      <AuthModal open={authOpen} initialMode={authMode} onClose={() => setAuthOpen(false)} />
    </header>
  );
};


/**
 * ProfileMenu — consolidated dropdown next to the avatar pill.
 *
 * Hosts everything that used to live as separate top-bar buttons:
 *  - Profile / Saved files / Change username
 *  - Admin Analytics + Manage + Exit admin (only when `admin` is true)
 *  - Sign out
 *
 * Pulling these out of the top bar fixes the header smoosh when coin balances
 * or notifications icons appear.
 */
function ProfileMenu({ user, admin }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const nav = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const go = (path) => { setOpen(false); nav(path); };

  return (
    <div className="relative ml-1.5 shrink-0" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-profile-menu-btn"
        title={`Signed in as ${user.username} — open menu`}
        className={`inline-flex items-center gap-1.5 px-2.5 xl:px-3 py-2 whitespace-nowrap border font-mono text-[10px] tracking-[0.15em] xl:tracking-[0.2em] uppercase transition-colors max-w-[180px] ${
          admin
            ? "border-[#ffd60a]/60 text-[#ffd60a] hover:bg-[#ffd60a]/10"
            : "border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
        }`}
      >
        {user.ai_avatar_id ? (
          <AvatarDisplay aiAvatarId={user.ai_avatar_id} username={user.username} size={20} className="shrink-0" />
        ) : user.avatar_id && AVATAR_ICONS[user.avatar_id] ? (
          <AvatarDisplay avatarId={user.avatar_id} username={user.username} size={13} className="shrink-0" />
        ) : (
          <UserCircle2 size={11} className="shrink-0" />
        )}
        <span className="truncate">{user.username}</span>
        {admin && <span className="ml-0.5 px-1 bg-[#ffd60a] text-[#050505] text-[8px] font-black tracking-wide">ADMIN</span>}
        <ChevronDown size={11} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-60 border-2 border-white/15 bg-[#0a0a0a] shadow-[0_8px_32px_rgba(0,0,0,0.85)] z-[120]"
          role="menu"
          data-testid="nav-profile-menu"
        >
          <div className="px-3 py-2 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">
            Signed in as <span className="text-white">@{user.username}</span>
          </div>
          <MenuItem icon={UserCircle2}    label="My Profile"      testid="menu-profile" onClick={() => go("/profile")} />
          <MenuItem icon={Bookmark}       label="Saved Files"     testid="menu-saved"   onClick={() => go("/bookmarks")} />
          <MenuItem icon={AtSign}         label="Change Username" testid="menu-rename"  onClick={() => go("/profile?tab=settings")} />
          {admin && (
            <>
              <div className="px-3 py-1.5 border-t border-white/10 font-mono text-[10px] tracking-[0.3em] text-[#ffd60a] uppercase bg-[#ffd60a]/5">
                <ShieldCheck size={10} className="inline mr-1" /> Admin
              </div>
              <MenuItem icon={Wrench}   label="Manage Files" testid="menu-admin-manage"   onClick={() => go("/admin/manage")}    accent="#a78bfa" />
              <MenuItem icon={Shield}   label="Whistleblower Tips" testid="menu-admin-drops" onClick={() => go("/admin/drops")}  accent="#ff3b30" />
              <MenuItem
                icon={ShieldCheck}
                label="Exit Admin"
                testid="menu-admin-exit"
                onClick={() => { clearAdmin(); setOpen(false); toast.success("Exited admin mode"); }}
                accent="#ff3b30"
              />
            </>
          )}
          <div className="border-t border-white/10" />
          <MenuItem
            icon={LogOut}
            label="Sign Out"
            testid="menu-logout"
            onClick={async () => { setOpen(false); await logout(); toast.success("Signed out"); }}
            accent="#ff3b30"
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, accent }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2 px-3 py-2 text-left font-mono text-[11px] tracking-[0.1em] uppercase text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
      style={accent ? { color: accent } : undefined}
    >
      <Icon size={12} className="shrink-0" />
      {label}
    </button>
  );
}

export default Navbar;
